import { cellAtWorld, heroWalkable } from '../grid.ts';
import { cosA, idiv, mulBp, sinA, withinRange } from '../math/fixed.ts';
import { rngChance, rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, Hero, Zone } from '../types.ts';
import { damageEnemy, damageHero, enemiesNear, ev, healHero } from './combat.ts';

/*
 * The day's omens that play on the field (SPEC §13.4): the storm's lightning, marked on the ground a moment
 * before it falls and hitting heroes and nightmares alike, and the falling stars, which land a few steps
 * from a hero and empower whoever gets there first.
 */

/** A struck bolt lingers this long for the eye, harmless. */
const AFTERGLOW = 6;

const standing = (s: BattleState): Hero[] => s.heroes.filter((h) => h.downTicks === 0);
const fighting = (s: BattleState): boolean => s.enemies.some((e) => e.hp > 0 && e.kind !== 'boneWall');

/** Each tick: a bolt now and then, a star now and then, while there is a fight. */
export const omenTick = (s: BattleState): void => {
  const r = s.rules;
  if ((!r?.bolts && !r?.stars) || s.result !== 'running' || !fighting(s)) return;
  const heroes = standing(s);
  if (!heroes.length) return;
  const b = r.bolts;
  if (b && s.tick % b.every === 0) {
    // Mostly near a hero (step aside); sometimes on a nightmare close to the heroes (lead the rest under it).
    const near = enemiesNear(s, (heroes[0] as Hero).x, (heroes[0] as Hero).y, 7000, (e) => e.kind !== 'boneWall');
    let x: number, y: number;
    if (near.length && rngChance(s.rng, b.onEnemyBp)) {
      const e = near[rngInt(s.rng, near.length)] as (typeof near)[number];
      x = e.x;
      y = e.y;
    } else {
      const h = heroes[rngInt(s.rng, heroes.length)] as Hero;
      x = h.x + rngRange(s.rng, -500, 500);
      y = h.y + rngRange(s.rng, -500, 500);
    }
    s.zones.push({ id: s.nextId++, kind: 'bolt', x, y, radius: b.radius, cells: [], ticks: b.warn + AFTERGLOW, every: 1, damage: b.heroDamage, slowBp: 0, pid: '', warn: b.warn });
  }
  const st = r.stars;
  if (st && s.tick % st.every === idiv(st.every, 2) && !s.zones.some((z) => z.kind === 'star')) {
    const h = heroes[rngInt(s.rng, heroes.length)] as Hero;
    const spot = starSpot(s, h, st.minDist, st.maxDist);
    s.zones.push({ id: s.nextId++, kind: 'star', x: spot.x, y: spot.y, radius: st.radius, cells: [], ticks: st.warn + st.ticks, every: 1, damage: st.impactDamage, slowBp: 0, pid: '', warn: st.warn });
  }
};

/** A few steps from the hero, on ground a hero can walk (closer in if there is none that far). */
const starSpot = (s: BattleState, h: Hero, lo: number, hi: number): { x: number; y: number } => {
  for (let k = 0; k < 8; k++) {
    const a = rngInt(s.rng, 1024);
    const d = idiv(rngRange(s.rng, lo, hi) * (8 - k), 8);
    const x = h.x + idiv(cosA(a) * d, 10000);
    const y = h.y + idiv(sinA(a) * d, 10000);
    const c = cellAtWorld(s, x, y);
    if (c >= 0 && heroWalkable(s, c)) return { x, y };
  }
  return { x: h.x, y: h.y };
};

/** The omens' zones: a bolt counts down and strikes; a star counts down, lands, and waits for a hero. */
export const omenZone = (s: BattleState, z: Zone): void => {
  if (z.warn === undefined) return;
  if (z.warn > 0) {
    z.warn--;
    if (z.warn > 0) return;
    if (z.kind === 'bolt') {
      for (const h of s.heroes) if (h.downTicks === 0 && withinRange(z.x, z.y, h.x, h.y, z.radius)) damageHero(s, h, z.damage);
      const hit = s.rules?.bolts?.enemyDamage ?? 0;
      for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => o.kind !== 'boneWall')) damageEnemy(s, e, hit, { pid: '', ignoresArmor: true });
      ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'bolt' });
      z.warn = -1;
      z.ticks = AFTERGLOW;
      return;
    }
    for (const e of enemiesNear(s, z.x, z.y, z.radius, (o) => o.kind !== 'boneWall')) damageEnemy(s, e, z.damage, { pid: '', ignoresArmor: true });
    ev(s, { t: 'boom', x: z.x, y: z.y, r: z.radius, kind: 'starfall' });
    z.warn = -1;
    return;
  }
  if (z.kind !== 'star' || z.warn !== -1) return;
  const st = s.rules?.stars;
  if (!st) return;
  // The first hero (by id) to stand on it takes its light.
  const h = s.heroes.find((o) => o.downTicks === 0 && withinRange(z.x, z.y, o.x, o.y, z.radius));
  if (!h) return;
  h.starUntil = s.tick + st.buffTicks;
  healHero(s, h, mulBp(h.stats.maxHp, st.healBp), h.pid);
  ev(s, { t: 'skill', hero: h.id, skill: 'starPick', x: z.x, y: z.y });
  z.ticks = 0;
};
